import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_release-nexus/artifacts/a0bdi5ug_Alignk%20backlink%20professional.png';

const TermsOfUse = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-teal-50">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-lg bg-white/80 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
              <img src={LOGO_URL} alt="Alignk Logo" className="h-12 w-auto" />
            </div>
            <Button variant="ghost" onClick={() => navigate('/')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
          Terms of Use
        </h1>
        <p className="text-gray-600 mb-8">Last Updated: January 2026</p>

        <div className="prose prose-lg max-w-none space-y-6">
          <section>
            <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              1. Acceptance of Terms
            </h2>
            <p className="text-gray-700 leading-relaxed">
              By accessing and using Alignk's press release distribution services, you accept and agree to be bound by these Terms of Use. If you do not agree to these terms, please do not use our services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              2. Service Description
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Alignk provides press release distribution and digital PR services to help businesses increase their brand visibility. Our services include distributing your news and announcements to media outlets and industry publications.
            </p>
          </section>

          <section className="bg-yellow-50 border-l-4 border-yellow-400 p-4 my-6">
            <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              3. Content Approval and Publication
            </h2>
            <div className="space-y-3">
              <p className="text-gray-700 leading-relaxed font-medium">
                <strong>IMPORTANT:</strong> All press releases submitted through our platform are subject to editorial approval by the respective media outlets and publications.
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>We facilitate the submission of your content to our network of publishing partners</li>
                <li>Each website has its own editorial guidelines and approval process</li>
                <li>We cannot guarantee that any specific website will approve or publish your content</li>
                <li>Publication timelines vary by website and are subject to their individual review processes</li>
                <li>Websites reserve the right to reject content that does not meet their quality standards or editorial guidelines</li>
              </ul>
              <p className="text-gray-700 leading-relaxed font-medium mt-3">
                By purchasing our services, you acknowledge and accept that content approval is at the sole discretion of the publishing websites and not Alignk.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              4. User Obligations
            </h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              You agree to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Provide accurate and complete information</li>
              <li>Submit only original content that you own or have rights to distribute</li>
              <li>Ensure your content does not violate any laws or third-party rights</li>
              <li>Not submit content that is defamatory, obscene, or otherwise objectionable</li>
              <li>Comply with all applicable laws and regulations</li>
              <li>Not use our services for any illegal or unauthorized purpose</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              5. Content Guidelines
            </h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              Your content must:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Be newsworthy and professionally written</li>
              <li>Not contain spam, promotional language, or excessive self-promotion</li>
              <li>Not include misleading or false information</li>
              <li>Comply with industry standards and best practices</li>
              <li>Meet the quality standards of our publishing partners</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              6. Payment and Fees
            </h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>All fees are due at the time of purchase</li>
              <li>Prices are subject to change without notice</li>
              <li>Payment processing is handled by secure third-party providers</li>
              <li>You are responsible for any applicable taxes</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              7. Intellectual Property
            </h2>
            <p className="text-gray-700 leading-relaxed">
              You retain ownership of your content. By using our services, you grant Alignk and our publishing partners a non-exclusive license to distribute, publish, and display your content for the purpose of providing our services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              8. Service Availability
            </h2>
            <p className="text-gray-700 leading-relaxed">
              We strive to maintain continuous service availability but do not guarantee uninterrupted access. We reserve the right to modify, suspend, or discontinue any part of our services at any time without notice.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              9. Limitation of Liability
            </h2>
            <p className="text-gray-700 leading-relaxed">
              To the maximum extent permitted by law, Alignk shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of our services. Our total liability shall not exceed the amount you paid for the specific service in question.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              10. Disclaimer of Warranties
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Our services are provided "as is" without warranties of any kind, either express or implied. We do not guarantee specific results, rankings improvements, or publication acceptance rates.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              11. Account Termination
            </h2>
            <p className="text-gray-700 leading-relaxed">
              We reserve the right to suspend or terminate your account at any time for violation of these terms or for any other reason at our sole discretion.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              12. Changes to Terms
            </h2>
            <p className="text-gray-700 leading-relaxed">
              We may modify these Terms of Use at any time. Continued use of our services after changes constitutes acceptance of the modified terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              13. Governing Law
            </h2>
            <p className="text-gray-700 leading-relaxed">
              These terms shall be governed by and construed in accordance with applicable laws, without regard to conflict of law principles.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              14. Contact Information
            </h2>
            <p className="text-gray-700 leading-relaxed">
              For questions about these Terms of Use, please contact us at:
            </p>
            <p className="text-gray-700 mt-2">
              <strong>Email:</strong> support@alignk.com
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfUse;